import { DateFormatter, now, parseAbsolute } from "@internationalized/date"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useTranslation } from "react-i18next"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import { getLocale } from "@/utilities/etc"
import { normalizedAPILicensePlate } from "@/utilities/normalizers"
import { scopedTo } from "@/utilities/scopedRoutes"

import styles from "./reservationModals.module.css"

interface Props {
  isOpen: boolean
  onOpenChange: () => void
  reservation: ReservationMini
  onOpenCancelModal?: () => void
  qrLookupID?: string
  contextLocationID?: string | null
}

export function InfoModal({
  isOpen,
  onOpenChange,
  reservation,
  onOpenCancelModal,
  qrLookupID,
  contextLocationID,
}: Props) {
  const { t, i18n } = useTranslation("reserve")
  const locale = i18n.resolvedLanguage || getLocale()
  const timezoneLocation = reservation.locationTimezone
  const calendarName = reservation.bookingCalendarName
  const rateName = reservation.inventoryTreeRateName

  const sessionStart = parseAbsolute(
    reservation.startDateTime,
    timezoneLocation
  )
  const sessionEnd = parseAbsolute(reservation.endDateTime, timezoneLocation)
  const cancellationDeadline = reservation.cancellationDeadline
    ? parseAbsolute(reservation.cancellationDeadline, timezoneLocation)
    : null
  const canCancel =
    !reservation.blockCancellation &&
    cancellationDeadline != null &&
    now(timezoneLocation).compare(cancellationDeadline) < 0

  /*
   * Same test as ReservationCard: only theme the receipt when the reservation
   * belongs to the location we're currently scoped to. Both IDs must be
   * present — `reservation.locationID` is optional on some variants, so
   * comparing two absent values would otherwise read as a match.
   */
  const useThemedReceipt =
    !!qrLookupID &&
    !!contextLocationID &&
    contextLocationID === reservation.locationID

  const dateTimeFormatter = new DateFormatter(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    timeZone: timezoneLocation,
  })

  const formattedStart = dateTimeFormatter.format(sessionStart.toDate())
  const formattedEnd = dateTimeFormatter.format(sessionEnd.toDate())

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Header>
        <Heading level={2} slot="title">
          {calendarName}
        </Heading>
      </Modal.Header>
      <Modal.Body className={styles.infoModalBody}>
        <div className={styles.infoRow}>
          <Text level="disclaimer" className={styles.infoLabel}>
            {t("confirmation.rate")}
          </Text>
          <Text level={1}>{rateName}</Text>
        </div>
        <div className={styles.infoRow}>
          <Text level="disclaimer" className={styles.infoLabel}>
            {t("confirmation.start")}
          </Text>
          <Text level={1}>{formattedStart}</Text>
        </div>
        <div className={styles.infoRow}>
          <Text level="disclaimer" className={styles.infoLabel}>
            {t("confirmation.end")}
          </Text>
          <Text level={1}>{formattedEnd}</Text>
        </div>
        <div className={styles.infoRow}>
          <Text level="disclaimer" className={styles.infoLabel}>
            {t("confirmation.vehiclePlate")}
          </Text>
          <Text level={1}>{normalizedAPILicensePlate(reservation.plate)}</Text>
        </div>
        <div className={styles.infoRow}>
          <Text level="disclaimer" className={styles.infoLabel}>
            {t("confirmation.confirmationNumber")}
          </Text>
          <Text level={1}>{reservation.confirmationNumber}</Text>
        </div>
        {reservation.blockCancellation ? (
          <div className={styles.infoRow}>
            <Text level="disclaimer" className={styles.infoLabel}>
              {t("confirmation.cancellation")}
            </Text>
            <Text level={1}>{t("confirmation.cannotCancel")}</Text>
          </div>
        ) : (
          cancellationDeadline && (
            <div className={styles.infoRow}>
              <Text level="disclaimer" className={styles.infoLabel}>
                {t("confirmation.cancellationDeadline")}
              </Text>
              {canCancel ? (
                <Text level={1}>
                  {dateTimeFormatter.format(cancellationDeadline.toDate())}
                </Text>
              ) : (
                <Text level={1}>{t("confirmation.cannotCancel")}</Text>
              )}
            </div>
          )
        )}
      </Modal.Body>
      <Modal.Footer arrangement="vertical">
        {() => (
          <>
            {reservation.receiptIDs?.length > 0 && (
              <LinkButton
                buttonType="primary"
                {...scopedTo(
                  "/receipt/$receiptID",
                  useThemedReceipt ? qrLookupID : undefined,
                  { receiptID: reservation.receiptIDs[0] }
                )}
              >
                {t("confirmation.viewReceipt")}
              </LinkButton>
            )}
            {onOpenCancelModal && canCancel && (
              <Text
                level={3}
                className="center"
                style={{ color: "var(--danger-11)" }}
              >
                <Button
                  buttonType="danger"
                  presentation="link"
                  style={{ marginTop: "var(--space-4)" }}
                  onPress={() => {
                    onOpenChange()
                    onOpenCancelModal()
                  }}
                >
                  {t("confirmation.cancelReservation")}
                </Button>
              </Text>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
